import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Grid, Button } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompanyDetails } from '../../../redux/company/types';
import { isEmpty } from '../../../utils/lodash';
import { formatCurrencyWithAbbrevation } from '../../../constants/currency';
import { convertMinuteSecondsDays } from '../../../utils/AppUtils';
import Pagination from '../../Common/Pagination';
import { useDispatch } from 'react-redux';
import { getCompanyDetails } from '../../../redux/company/actions';
import { useParams } from 'react-router-dom';
import { IRole } from '../../../types/global/helper';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { navigate } from '../../../redux/navigator/action';
import { ROUTES } from '../../../types/global/routes.types';
import { RichTextViewer } from '../../Common/RichTextViewer';
import { COMPANY_BRAND_ON_ORANGE, COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface IAuditionsTab {
    companyDetails?: ICompanyDetails;
}

const NEW_MS = 14 * 24 * 60 * 60 * 1000;

const isNewAudition = (audition: IRole['audition']) => {
    const raw = audition.createdAt || audition.created || audition.time_created;
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return false;
    return Date.now() - t < NEW_MS;
};

const labelSx = {
    fontSize: '10px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.55)',
    fontFamily: FONT_ENUM.MEDIUM_TEXT,
    mb: 0.5,
};

const AuditionsTab: React.FC<IAuditionsTab> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const params = useParams();
    const { id } = params;
    const { data, pagination } = companyDetails?.auditions! || {};

    const handlePageChange = (page: number) => {
        if (id) {
            dispatch(
                getCompanyDetails({
                    slug: id,
                    auditionsPage: page,
                    auditionsLimit: pagination?.pageSize || 10,
                    withAuditions: true,
                    singleFetch: true,
                }),
            );
        }
    };

    const handleViewDetails = (role: IRole) => {
        const route = replaceRouteParam(ROUTES.AUDITION_DETAIL, 'auditionId', role.audition.slug!);
        dispatch(
            navigate({
                routes: route,
            }),
        );
    };

    if (!data || isEmpty(data)) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'rgba(255,255,255,0.55)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MEDIUM_TEXT, fontSize: '1rem' }}>
                    No auditions available at the moment.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: { xs: 3, md: 5 } }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                        mb: 1,
                    }}>
                    Open Auditions ({pagination?.totalCount ?? data.length})
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                    }}>
                    Current casting calls and talent searches
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
                {data.map((role, index) => {
                    const audition = role.audition;
                    const hasRichDescription = Boolean(
                        audition.synopsis?.trim() || audition.description?.trim(),
                    );
                    const showNew = isNewAudition(audition);

                    const ageLabel =
                        audition.min_age && audition.max_age
                            ? `${audition.min_age}-${audition.max_age}`
                            : audition.min_age
                            ? `${audition.min_age}+`
                            : 'Any';

                    const locationText = audition.shoot_location?.address || 'TBD';
                    const compensationText =
                        formatCurrencyWithAbbrevation(audition.max_reward, audition.currency) || 'TBD';
                    const deadlineText = audition.expiration
                        ? `in ${convertMinuteSecondsDays(audition.casting_expiration)}`
                        : 'in TBD';

                    return (
                        <Card
                            key={audition._id || index}
                            sx={{
                                position: 'relative',
                                bgcolor: '#131313',
                                border: '1px solid transparent',
                                borderRadius: 3,
                                boxShadow: 'none',
                                overflow: 'hidden',
                                transition: 'border-color 0.25s ease',
                                '&:hover': {
                                    borderColor: 'rgba(255,156,40,0.22)',
                                },
                                '&:hover .company-audition-title': {
                                    color: COMPANY_BRAND_ORANGE,
                                },
                            }}>
                            <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        alignItems: { xs: 'stretch', md: 'flex-start' },
                                        justifyContent: 'space-between',
                                        gap: { xs: 2.5, md: 3 },
                                    }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {showNew && (
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    px: 1.5,
                                                    py: 0.5,
                                                    mb: 2,
                                                    borderRadius: 1,
                                                    bgcolor: `${COMPANY_BRAND_ORANGE}1a`,
                                                    border: `1px solid ${COMPANY_BRAND_ORANGE}33`,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.14em',
                                                        textTransform: 'uppercase',
                                                        color: COMPANY_BRAND_ORANGE,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    New Audition
                                                </Typography>
                                            </Box>
                                        )}

                                        <Typography
                                            className="company-audition-title"
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.BOLD_HEADER,
                                                fontWeight: 700,
                                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                mb: hasRichDescription ? 1.5 : 0,
                                                transition: 'color 0.2s ease',
                                            }}>
                                            {audition.title}
                                        </Typography>

                                        {hasRichDescription && (
                                            <Box
                                                sx={{
                                                    mb: 2,
                                                    maxWidth: 640,
                                                    '& .ql-editor': { padding: 0 },
                                                }}>
                                                <RichTextViewer
                                                    content={audition.synopsis || audition.description || ''}
                                                    lineClamp={2}
                                                    textColor="rgba(255,255,255,0.55)"
                                                    textStyle={{
                                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                        fontSize: '0.875rem',
                                                        lineHeight: 1.6,
                                                        margin: 0,
                                                        padding: 0,
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 3 }}>
                                            <Grid item xs={6} md={3}>
                                                <Box
                                                    sx={{
                                                        textAlign: { xs: 'left', md: 'center' },
                                                    }}>
                                                    <Typography sx={labelSx}>Age Range</Typography>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                        }}>
                                                        {ageLabel}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                                                    <Typography sx={labelSx}>Location</Typography>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            wordBreak: 'break-word',
                                                        }}>
                                                        {locationText}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                                                    <Typography sx={labelSx}>Compensation</Typography>
                                                    <Typography
                                                        sx={{
                                                            color: COMPANY_BRAND_ORANGE,
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontWeight: 700,
                                                            fontSize: '0.875rem',
                                                        }}>
                                                        {compensationText}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                                                    <Typography sx={labelSx}>Deadline</Typography>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            fontStyle: 'italic',
                                                        }}>
                                                        {deadlineText}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {(audition.required_skill?.length || audition.tags?.length) ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                    mt: 2.5,
                                                }}>
                                                {audition.required_skill?.map((skill, skillIndex) => (
                                                    <Chip
                                                        key={`s-${skillIndex}`}
                                                        label={skill}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'rgba(255,255,255,0.12)',
                                                            color: 'rgba(255,255,255,0.65)',
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontSize: '0.7rem',
                                                            height: 26,
                                                        }}
                                                    />
                                                ))}
                                                {audition.tags?.map((tag, tagIndex) => (
                                                    <Chip
                                                        key={`t-${tagIndex}`}
                                                        label={tag}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderColor: 'rgba(255,255,255,0.12)',
                                                            color: 'rgba(255,255,255,0.65)',
                                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                            fontSize: '0.7rem',
                                                            height: 26,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        ) : null}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: { xs: 'stretch', md: 'center' },
                                            justifyContent: { xs: 'stretch', md: 'flex-end' },
                                            flexShrink: 0,
                                        }}>
                                        <Button
                                            onClick={() => handleViewDetails(role)}
                                            sx={{
                                                borderRadius: 9999,
                                                px: 4,
                                                py: 1.5,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: '0.875rem',
                                                bgcolor: '#262626',
                                                color: '#ffffff',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                                width: { xs: '100%', md: 'auto' },
                                                minWidth: { md: 160 },
                                                '&:hover': {
                                                    bgcolor: COMPANY_BRAND_ORANGE,
                                                    color: COMPANY_BRAND_ON_ORANGE,
                                                    boxShadow: '0 8px 24px rgba(255,156,40,0.25)',
                                                },
                                            }}>
                                            View Details
                                        </Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {pagination && (pagination.hasNextPage || pagination.hasPrevPage) && (
                <Pagination
                    variant="company"
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={page => {
                        handlePageChange(page);
                    }}
                    hasNextPage={pagination.hasNextPage}
                    hasPreviousPage={pagination.hasPrevPage}
                />
            )}
        </Box>
    );
};

export { AuditionsTab };
